/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    CacheType,
    IQueryResultSettings,
    InvoicePaidStatus,
    InvoiceSearchCriteria,
    InvoiceType,
    OrderInvoice,
    SalesOrdersDataActions
} from '@msdyn365-commerce/retail-proxy';
import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { QueryResultSettingsProxy, getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
import { IGeneric, IAny, createObservableDataAction } from '@msdyn365-commerce/core';
import { IInvoiceTypesData } from '../modules/gdn-invoices-list/gdn-invoices-list.props.autogenerated';

const getDefaultSearchCriteria = (): InvoiceSearchCriteria => {
    return {
        InvoicePaidStatusValues: [InvoicePaidStatus.None]
    };
};

/**
 * GetGdnInvoices Input Action
 */
export class GetGdnInvoicesInput implements Msdyn365.IActionInput {
    // // TODO: Construct the input needed to run the action
    // constructor() {}

    // // TODO: Determine if the results of this get action should cache the results and if so provide
    // // a cache object type and an appropriate cache key
    // public getCacheKey = () => `TODO`;
    // public getCacheObjectType = () => 'TODO';
    // public dataCacheType = (): Msdyn365.CacheType => 'application';
    public readonly searchCriteria: InvoiceSearchCriteria;

    public readonly queryResultSettings: IQueryResultSettings;

    public readonly getInvoiceType: IInvoiceTypesData[];

    constructor(searchCriteria: InvoiceSearchCriteria, queryResultSettings: IQueryResultSettings, getInvoiceType: IInvoiceTypesData[]) {
        this.searchCriteria = searchCriteria;
        this.queryResultSettings = queryResultSettings;
        this.getInvoiceType = getInvoiceType;
    }

    public getCacheKey = () => 'BusinessPartnerInvoices';

    public getCacheObjectType = () => 'OrderInvoice';

    public dataCacheType = (): CacheType => 'none';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetGdnInvoicesData {
    text: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createGdnGetInvoicesInput = (inputData: Msdyn365.ICreateActionContext<IGeneric<IAny>>): Msdyn365.IActionInput => {
    //return new GetGdnInvoicesInput();
    const { requestContext } = inputData;
    const invoiceType = inputData.config?.invoiceTypes;
    // Check if the user is authenticated.
    if (!requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;

    return new GetGdnInvoicesInput(getDefaultSearchCriteria(), queryResultSettings, invoiceType);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function getGdnInvoices(input: GetGdnInvoicesInput, context: Msdyn365.IActionContext): Promise<OrderInvoice[]> {
    // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    // TODO: Uncomment the below line to get the value from a service
    // const response = await Msdyn365.sendRequest<IGetGdnInvoicesData[]>('/get/example/id/1', 'get');
    // return { text: 'Static data from action' };

    if (!context?.requestContext?.user?.isAuthenticated) {
        throw new Error('Unable to get list of invoices. User is not authenticated.');
    }

    let invoiceSearch: number[] = [];

    if (input.getInvoiceType !== undefined) {
        for (let i = 0; i < input.getInvoiceType.length; i++) {
            switch (input.getInvoiceType[i].invoiceType) {
                case 'None':
                    invoiceSearch = [...invoiceSearch, InvoiceType.None];
                    break;
                case 'SalesOrderInvoice':
                    invoiceSearch = [...invoiceSearch, InvoiceType.SalesOrderInvoice];
                    break;
                case 'FreeTextInvoice':
                    invoiceSearch = [...invoiceSearch, InvoiceType.FreeTextInvoice];
                    break;
                case 'ProjectInvoice':
                    invoiceSearch = [...invoiceSearch, InvoiceType.ProjectInvoice];
                    break;
                case 'CreditNoteInvoice':
                    invoiceSearch = [...invoiceSearch, InvoiceType.CreditNoteInvoice];
                    break;
                case 'FreeTextCreditNoteInvoice':
                    invoiceSearch = [...invoiceSearch, InvoiceType.FreeTextCreditNoteInvoice];
                    break;
                case 'ProjectCreditNoteInvoice':
                    input.searchCriteria.InvoiceTypeValues = [InvoiceType.ProjectCreditNoteInvoice];
                    break;
            }
        }

        input.searchCriteria.InvoiceTypeValues = invoiceSearch;

        if (invoiceSearch.length === 0) {
            input.searchCriteria.InvoiceTypeValues = [InvoiceType.SalesOrderInvoice];
        }
    } else {
        input.searchCriteria.InvoiceTypeValues = [InvoiceType.SalesOrderInvoice];
    }

    const businessPartner = await getCurrentAsync({ callerContext: context });
    input.searchCriteria.BusinessPartnerId = businessPartner?.BusinessPartnerId;

    const customer = await getCustomer(new GetCustomerInput(context.requestContext.apiSettings), context);
    input.searchCriteria.CustomerId = customer.AccountNumber;

    return SalesOrdersDataActions.getInvoicesAsync(
        { callerContext: context, queryResultSettings: input.queryResultSettings },
        input.searchCriteria
    );
}

export const getGdnInvoicesDataAction = createObservableDataAction({
    action: <Msdyn365.IAction<OrderInvoice[]>>getGdnInvoices,
    // TODO: Give your data action a unique id
    id: 'get-gdn-invoices',
    input: createGdnGetInvoicesInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});

export default getGdnInvoicesDataAction;
